import { useLayoutEffect, useState } from 'react';

import { centerInScrollPorts, findScrollPort } from './scrollPort';

/**
 * Scroll to a form field by its name (the value passed to react-hook-form's
 * `register()`) and briefly highlight it so the user sees what needs attention.
 *
 * Used by the Push to EMR step to take users from a validation error to the
 * offending field when it is visible on the current tab.
 *
 * Lookup precedence:
 *   1. `[data-field-anchor="<name>"]` — set via DateField `fieldAnchor` or a
 *      wrapper for other non-input controls (e.g. portaled widgets).
 *   2. `[name="<name>"]` — works for inputs registered through react-hook-form,
 *      including hidden inputs inside `FormSelect` / `VirtualizedFormSelect`.
 *   3. `getElementById(name)` — fallback for fields whose visible input id
 *      already matches the field name.
 *
 * When the located element is a hidden input (e.g. inside a virtualized
 * combobox), we scroll to and highlight its visible parent container instead.
 */

const HIGHLIGHT_CLASSES = [
  'ring-2',
  'ring-inset',
  'ring-red-500',
  'rounded-md',
  'transition',
];
const HIGHLIGHT_DURATION_MS = 2500;

function isHiddenInput(el: Element | null): el is HTMLInputElement {
  // The design system's FormSelect/VirtualizedFormSelect render an explicit
  // <input type="hidden" name={field} /> so users can scroll to the visible
  // container around it. Other "visually hidden but technically rendered"
  // inputs (e.g. screen-reader-only checkboxes) should not be redirected.
  return el instanceof HTMLInputElement && el.type === 'hidden';
}

function findScrollTarget(name: string): HTMLElement | null {
  if (typeof document === 'undefined' || !name) return null;

  // Escape just enough to be safe inside a CSS attribute selector. Field names
  // in this app are snake_case, but defensive escaping is cheap.
  const safe = name.replace(/"/g, '\\"');

  const byAnchor = document.querySelector(
    `[data-field-anchor="${safe}"]`
  ) as HTMLElement | null;
  if (byAnchor) return byAnchor;

  const byName = document.querySelector(
    `[name="${safe}"]`
  ) as HTMLElement | null;
  if (byName) {
    // For hidden inputs (e.g. inside a virtualized combobox), the input itself
    // can't be scrolled to — use the visible container instead.
    if (isHiddenInput(byName)) {
      return (byName.parentElement as HTMLElement | null) ?? byName;
    }
    return byName;
  }

  return document.getElementById(name);
}

/** Whether `scrollToField(name)` would find a target in the current document. */
export function canScrollToField(name: string): boolean {
  return findScrollTarget(name) !== null;
}

/**
 * After layout, which of the given field names exist in the document. Use when
 * building validation bullets so links are only shown for fields on the current tab.
 */
export function useScrollableFields(fieldNames: string[]): ReadonlySet<string> {
  const namesKey = fieldNames.join('\0');
  const [scrollable, setScrollable] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  useLayoutEffect(() => {
    setScrollable(new Set(fieldNames.filter(canScrollToField)));
  }, [namesKey]);

  return scrollable;
}

export interface ScrollToFieldOptions {
  /** Whether to focus the element after scrolling. Defaults to true. */
  focus?: boolean;
  /** Whether to add a transient red outline. Defaults to true. */
  highlight?: boolean;
  /** Delay (ms) before running the lookup (e.g. after expand/collapse). */
  delayMs?: number;
}

export function scrollToField(
  name: string,
  options: ScrollToFieldOptions = {}
): boolean {
  const { focus = true, highlight = true, delayMs = 0 } = options;

  const run = (): boolean => {
    const target = findScrollTarget(name);
    if (!target) return false;

    // Inside a scrollable container (e.g. a dialog's main pane) move only the
    // boxes that really scroll, each outward from the nearest: scrollIntoView
    // also scrolls overflow: hidden ancestors, such as the dialog's own content
    // box, which pushes its header out of view and leaves blank space until the
    // dialog is reopened (EMR-2690). A scroll box nested in another one (the
    // intake form pane inside the app shell) needs both moved.
    if (findScrollPort(target)) {
      centerInScrollPorts(target);
    } else {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (
      focus &&
      typeof (target as HTMLElement & { focus?: () => void }).focus ===
        'function'
    ) {
      try {
        (target as HTMLElement).focus({ preventScroll: true });
      } catch {
        // focus() can throw on certain non-focusable elements — ignore.
      }
    }

    if (highlight) {
      target.classList.add(...HIGHLIGHT_CLASSES);
      window.setTimeout(() => {
        target.classList.remove(...HIGHLIGHT_CLASSES);
      }, HIGHLIGHT_DURATION_MS);
    }

    return true;
  };

  if (delayMs > 0) {
    window.setTimeout(run, delayMs);
    return true;
  }
  return run();
}
